import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Users } from 'lucide-react';
import { useTranslation } from '../Context/TranslationContext';

const YouthProgramSection = () => {
  const { isTamil } = useTranslation();

  const translations = {
    title: {
      en: "Youth Programme",
      ta: "இளைஞர் திட்டம்"
    },
    description: {
      en: "Empowering young minds through structured development programs that build character, leadership, and life skills",
      ta: "குணாதிசயம், தலைமைத்துவம் மற்றும் வாழ்க்கைத் திறன்களை வளர்க்கும் கட்டமைக்கப்பட்ட மேம்பாட்டுத் திட்டங்கள் மூலம் இளம் மனங்களுக்கு அதிகாரமளித்தல்"
    }
  };

  const programs = [
    {
      id: 'bunny',
      name: { en: "Bunny", ta: "பன்னி" },
      ageRange: { en: "Ages: 3 to 6 yrs", ta: "வயது: 3 முதல் 6 வயது வரை" },
      motto: { en: "Keep Smiling", ta: "புன்னகையுடன் இருங்கள்" },
      image: "/Images/bunny.png",
      color: "from-[#FF6B6B] to-[#FF8787]",
      route: "/bunny"
    },
    {
      id: 'cubs',
      name: { en: "Cubs", ta: "கப்ஸ்" },
      ageRange: { en: "Ages: 5+ to 10 yrs", ta: "வயது: 5+ முதல் 10 வயது வரை" },
      motto: { en: "Do Your Best", ta: "சிறப்பாக செய்யுங்கள்" },
      image: "/Images/cubs.png",
      color: "from-[#FFB94E] to-[#FFD93D]",
      route: "/cubs"
    },
    {
      id: 'bulbuls',
      name: { en: "BulBuls", ta: "புல்புல்" },
      ageRange: { en: "Ages: 5+ to 10 yrs", ta: "வயது: 5+ முதல் 10 வயது வரை" },
      motto: { en: "Do Your Best", ta: "சிறப்பாக செய்யுங்கள்" },
      image: "/Images/bulbul.png",
      color: "from-[#4E94FF] to-[#3D7EFF]",
      route: "/bulbuls"
    },
    {
      id: 'scouts',
      name: { en: "Scouts", ta: "சாரணர்" },
      ageRange: { en: "Ages: 10+ to 17 yrs", ta: "வயது: 10+ முதல் 17 வயது வரை" },
      motto: { en: "Be Prepared", ta: "தயாராக இருங்கள்" },
      image: "/Images/scouts.png",
      color: "from-[#2C251D] to-[#8A7968]",
      route: "/scouts"
    },
    {
      id: 'guide',
      name: { en: "Guide", ta: "சாரணியர்" },
      ageRange: { en: "Ages: 10+ to 17 yrs", ta: "வயது: 10+ முதல் 17 வயது வரை" },
      motto: { en: "Be Prepared", ta: "தயாராக இருங்கள்" },
      image: "/Images/guide.png",
      color: "from-[#8A7968] to-[#2C251D]",
      route: "/guide"
    },
    {
      id: 'rover',
      name: { en: "Rover", ta: "ரோவர்" },
      ageRange: { en: "Ages: 15 to 25 yrs", ta: "வயது: 15 முதல் 25 வயது வரை" },
      motto: { en: "Service", ta: "சேவை" },
      image: "/Images/rover.png",
      color: "from-[#726354] to-[#2C251D]",
      route: "/rover"
    },
    {
      id: 'ranger',
      name: { en: "Ranger", ta: "ரேஞ்சர்" },
      ageRange: { en: "Ages: 15 to 25 yrs", ta: "வயது: 15 முதல் 25 வயது வரை" },
      motto: { en: "Service", ta: "சேவை" },
      image: "/Images/ranger.png",
      color: "from-[#2C251D] to-[#726354]",
      route: "/ranger"
    }
  ];

  return (
    <section className="py-24 bg-[#FDFBF8]">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <span className="text-[#8A7968] font-semibold tracking-wider uppercase text-sm mb-4 block">
            {isTamil ? "திட்டங்கள்" : "Programs"}
          </span>
          <h2 className={`text-[#2C251D] font-bold mb-6 ${
            isTamil ? 'text-3xl md:text-4xl' : 'text-4xl md:text-5xl'
          }`}>
            {translations.title[isTamil ? 'ta' : 'en']}
          </h2>
          <div className="w-24 h-1.5 bg-gradient-to-r from-[#8A7968] to-[#726354] mx-auto rounded-full mb-6" />
          <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
            {translations.description[isTamil ? 'ta' : 'en']}
          </p>
        </motion.div>

        {/* Programs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {programs.map((program, index) => (
            <motion.div
              key={program.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Link to={program.route}>
                <div className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl 
                              transition-all duration-500 transform hover:-translate-y-1">
                  {/* Card Header with Gradient */}
                  <div className={`h-32 bg-gradient-to-br ${program.color} relative overflow-hidden`}>
                    <div className="absolute inset-0 opacity-10">
                      <Users className="w-40 h-40 text-white transform rotate-12 translate-x-20 -translate-y-10" />
                    </div>
                  </div>

                  {/* Program Icon/Image */}
                  <div className="relative -mt-16 mb-6 px-6">
                    <div className="w-28 h-28 rounded-2xl bg-white shadow-xl p-4 backdrop-blur-xl">
                      <img
                        src={program.image}
                        alt={program.name[isTamil ? 'ta' : 'en']}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="px-6 pb-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-[#2C251D] mb-2">
                          {program.name[isTamil ? 'ta' : 'en']}
                        </h3>
                        <p className="text-sm text-gray-500 font-medium">
                          {program.ageRange[isTamil ? 'ta' : 'en']}
                        </p>
                      </div>
                      <motion.div
                        whileHover={{ x: 5 }}
                        transition={{ duration: 0.2 }}
                        className="mt-1"
                      >
                        <ChevronRight className="w-5 h-5 text-[#8A7968]" />
                      </motion.div>
                    </div>
                    <div className="pt-4 border-t border-gray-100">
                      <p className="text-sm font-semibold text-[#8A7968]">
                        {program.motto[isTamil ? 'ta' : 'en']}
                      </p>
                    </div>
                  </div>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-5 
                                transition-opacity duration-500" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default YouthProgramSection;